import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, Output, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';

import { AssistanceResource } from '../../../../../core/interfaces/assistance-resource';
import {
  embedUrl,
  kindIcon,
  resourceKind,
} from '../../../../../core/utils/assistance-media';
import { isNavigableUrl } from '../../../../../core/utils/external-link';

/** `embed` : vidéo hébergée, bridée ; `pdf` : document signé, sous la visionneuse du navigateur. */
type Display = 'embed' | 'pdf' | 'video' | 'image' | 'fallback';

/**
 * Lecteur d'une ressource d'aide, dans la série de son module.
 *
 * <p>Partagé par l'aperçu de la gestion et par la consultation : un seul
 * endroit décide de ce qu'une URL a le droit de devenir dans une iframe, et
 * la navigation Précédent / Suivant suit la même série des deux côtés.</p>
 */
@Component({
  selector: 'app-assistance-player',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './assistance-player.component.html',
  styleUrl: './assistance-player.component.css',
})
export class AssistancePlayerComponent implements OnChanges {
  private readonly sanitizer = inject(DomSanitizer);

  @Input({ required: true }) resource!: AssistanceResource;
  /** La série du module, dans son ordre. */
  @Input() series: readonly AssistanceResource[] = [];
  @Input() moduleTitle = '';

  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly navigate = new EventEmitter<AssistanceResource>();
  @Output() readonly download = new EventEmitter<AssistanceResource>();

  display: Display = 'fallback';
  frameUrl: SafeResourceUrl | null = null;
  mediaUrl: string | null = null;
  linkUrl: string | null = null;
  icon = 'fa-solid fa-file';
  index = -1;

  ngOnChanges(): void {
    const resource = this.resource;
    const kind = resourceKind(resource);
    const embed = embedUrl(resource.externalUrl);
    const frame = embed ?? (kind === 'pdf' ? resource.previewUrl ?? null : null);
    const media = resource.previewUrl ?? resource.externalUrl ?? null;

    this.frameUrl = frame && isNavigableUrl(frame) ? this.sanitizer.bypassSecurityTrustResourceUrl(frame) : null;
    this.mediaUrl = media && isNavigableUrl(media) ? media : null;
    this.linkUrl = resource.externalUrl && isNavigableUrl(resource.externalUrl) ? resource.externalUrl : null;
    this.icon = kindIcon(resource);
    this.index = this.series.findIndex((r) => r.id === resource.id);

    if (this.frameUrl) this.display = embed ? 'embed' : 'pdf';
    else if (kind === 'video' && this.mediaUrl) this.display = 'video';
    else if (kind === 'image' && this.mediaUrl) this.display = 'image';
    else this.display = 'fallback';
  }

  get previous(): AssistanceResource | null {
    return this.index > 0 ? this.series[this.index - 1] : null;
  }

  get next(): AssistanceResource | null {
    return this.index >= 0 && this.index < this.series.length - 1 ? this.series[this.index + 1] : null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closed.emit();
  }
}
